// src/pages/AdminPage/AdminPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "hooks/useUser";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { db } from "services/firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
} from "firebase/firestore";
import toast from "react-hot-toast";
import UserTable from "components/admin/UserTable";
import { Button } from "components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import AdminCalendarManager from "components/AdminCalendarManager";
import AdminSettingsManager from "components/admin/AdminSettingsManager";
import { Eraser, Users, Calendar, Settings } from "lucide-react";

function AdminPage() {
  const { userProfile } = useUser();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCleaning, setIsCleaning] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error(`Erro ao buscar usuários: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCleanupPending = async () => {
    setIsCleaning(true);
    try {
      // 1. Buscar usuários pendentes
      const q = query(collection(db, "users"), where("role", "==", "pending"));

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast("Nenhum usuário pendente encontrado.", { icon: "ℹ️" });
        setIsCleaning(false);
        return;
      }

      // 2. Confirmação
      const confirmMessage = `Encontrados ${querySnapshot.size} usuários com status "pendente".\nDeseja realmente excluir TODOS eles?\nEssa ação não pode ser desfeita.`;

      if (!window.confirm(confirmMessage)) {
        setIsCleaning(false);
        return;
      }

      // 3. Executar deleção em massa (Batch)
      const batch = writeBatch(db);

      querySnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();

      toast.success(
        `${querySnapshot.size} usuários pendentes foram removidos!`,
      );

      // 4. Recarregar lista
      fetchUsers();
    } catch (err) {
      console.error("Erro na limpeza:", err);
      toast.error(`Erro ao limpar usuários: ${err.message}`);
    } finally {
      setIsCleaning(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === userProfile?.uid && newRole !== "admin") {
      toast.error(
        "Você não pode remover sua própria permissão de administrador.",
      );
      return;
    }
    try {
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, { role: newRole });
      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user,
        ),
      );
      toast.success("Função global do usuário atualizada!");
    } catch (err) {
      toast.error(`Falha ao atualizar função: ${err.message}`);
      console.error(err);
    }
  };

  const handlePermissionChange = async (userId, permission, value) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const updatePayload = {};
      updatePayload[`permissions.${permission}`] = value;

      await updateDoc(userDocRef, updatePayload);

      setUsers((currentUsers) =>
        currentUsers.map((user) => {
          if (user.id === userId) {
            return {
              ...user,
              permissions: {
                ...user.permissions,
                [permission]: value,
              },
            };
          }
          return user;
        }),
      );
      toast.success("Permissão de aba atualizada!");
    } catch (err) {
      toast.error(`Falha ao atualizar permissão: ${err.message}`);
      console.error(err);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === userProfile?.uid) {
      toast.error("Você não pode excluir sua própria conta de administrador.");
      return;
    }
    if (
      window.confirm(
        `Tem certeza que deseja excluir o usuário "${
          userName || "sem nome"
        }"? Esta ação não pode ser desfeita.`,
      )
    ) {
      try {
        const userDocRef = doc(db, "users", userId);
        await deleteDoc(userDocRef);
        setUsers((currentUsers) =>
          currentUsers.filter((user) => user.id !== userId),
        );
        toast.success("Usuário excluído com sucesso.");
      } catch (err) {
        toast.error(`Falha ao excluir usuário: ${err.message}`);
        console.error(err);
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full">
      <div className="text-center mb-2">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-foreground">
          Painel Administrativo
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie usuários e configurações do sistema.
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 h-auto sm:h-10 gap-2 sm:gap-0 max-w-[600px] bg-transparent sm:bg-muted p-0 sm:p-1">
          <TabsTrigger value="users" className="flex items-center justify-center gap-2 border sm:border-0 py-2 sm:py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground">
            <Users className="h-4 w-4" />
            Usuários
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center justify-center gap-2 border sm:border-0 py-2 sm:py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground">
            <Calendar className="h-4 w-4" />
            Calendário
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center justify-center gap-2 border sm:border-0 py-2 sm:py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-lg border shadow-sm dark:bg-card gap-4">
            <div>
              <h2 className="text-lg font-semibold">Base de Usuários</h2>
              <p className="text-sm text-muted-foreground">
                Gerencie permissões e acessos.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={handleCleanupPending}
              disabled={isCleaning}
              className="w-full sm:w-auto border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-900/10"
            >
              <Eraser className="mr-2 h-4 w-4" />
              {isCleaning ? "Limpando..." : "Limpar Pendentes Antigos"}
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-md border border-red-200">
              {error}
            </div>
          )}

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <UserTable
              users={users}
              currentUserUid={userProfile?.uid}
              onRoleChange={handleRoleChange}
              onDeleteUser={handleDeleteUser}
              onPermissionChange={handlePermissionChange}
            />
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <AdminCalendarManager />
        </TabsContent>

        <TabsContent value="settings">
          <AdminSettingsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AdminPage;
